/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, MessageSquare, Plus } from 'lucide-react';
import ConversationDepthBadge from '@/components/terra/ConversationDepthBadge';
import EcosystemBadge from '@/components/terra/EcosystemBadge';
import LeakChip from '@/components/terra/LeakChip';
import MovementPathCard from '@/components/terra/MovementPathCard';

const BAND_TONE = {
  IDEAL:     'bg-emerald-100 text-emerald-800 border-emerald-300',
  STRONG:    'bg-sky-100 text-sky-800 border-sky-300',
  NURTURE:   'bg-amber-100 text-amber-800 border-amber-300',
  HIGH_RISK: 'bg-rose-100 text-rose-800 border-rose-300',
};

export default function CounselorPipelinePage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [ecosystems, setEcosystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [bandFilter, setBandFilter] = useState('ALL');
  const [logFor, setLogFor] = useState(null);
  const [leakMap, setLeakMap] = useState({});

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, e, lk] = await Promise.all([
        axios.get(`${API}/terra-growth/counselor-pipeline/summary`),
        axios.get(`${API}/terra-growth/reference/conversation-depth`),
        axios.get(`${API}/terra-growth/ecosystems`),
        axios.get(`${API}/terra-growth/intelligence/leakage`).catch(() => ({ data: { leaks: [] } })),
      ]);
      setSummary(s.data); setTiers(t.data?.tiers || []); setEcosystems(e.data?.ecosystems || []);
      const lm = {};
      (lk.data?.leaks || []).forEach(l => { lm[l.lead_id] = l; });
      setLeakMap(lm);
    } catch { /* */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const ecoMap = ecosystems.reduce((m, e) => { m[e.code] = e; return m; }, {});
  const tierMap = tiers.reduce((m, t) => { m[t.code] = t; return m; }, {});

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (summary?.leads || []).filter(l => {
      if (bandFilter !== 'ALL' && l.rss_band !== bandFilter) return false;
      if (!q) return true;
      return [l.name, l.email, l.phone, l.city].some(v => (v || '').toLowerCase().includes(q));
    });
  }, [summary, search, bandFilter]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="counselor-pipeline-page" className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Counselor</p>
          <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Pipeline · depth-first</h1>
          <p className="text-sm text-[#8A8681] mt-1 max-w-xl">
            Pipeline rolled up by RSS band and last-conversation depth. 5 conversations ≠ 5 meaningful conversations.
          </p>
        </div>

        {/* Today's depth mix */}
        {summary && (
          <div data-testid="pipeline-depth-mix" className="rounded-2xl border border-[#EBE8E3] bg-white p-4 mb-5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] mb-2">Your conversation depth · last 7 days</p>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-3xl font-['Outfit'] tabular-nums text-[#2C2A29]">
                {summary.weekly_depth_mix?.deep_share_pct ?? 0}%
              </span>
              <span className="text-[11px] text-[#8A8681]">deep share</span>
              <div className="flex-1 flex flex-wrap gap-1">
                {Object.entries(summary.weekly_depth_mix?.buckets || {}).map(([code, n]) => (
                  <ConversationDepthBadge key={code} code={code}
                    tier={tierMap[code]?.tier} label={`${tierMap[code]?.label || code} · ${n}`} />
                ))}
                {summary.weekly_depth_mix?.total === 0 && (
                  <span className="text-[11px] text-[#B5B0A8]">No conversations logged yet this week.</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Input data-testid="pipeline-search" placeholder="Search by name / email / city…"
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 className="max-w-sm h-9 text-sm" />
          <select value={bandFilter} onChange={(e) => setBandFilter(e.target.value)}
                  data-testid="pipeline-band-filter"
                  className="h-9 rounded-md border border-[#D8D4CE] px-2 text-xs">
            <option value="ALL">All RSS bands</option>
            <option value="IDEAL">Ideal</option>
            <option value="STRONG">Strong</option>
            <option value="NURTURE">Nurture</option>
            <option value="HIGH_RISK">High-risk</option>
          </select>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Loading…
          </div>
        ) : (
          <div data-testid="pipeline-list" className="rounded-2xl border border-[#EBE8E3] bg-white overflow-hidden">
            <table className="w-full text-sm font-['Work_Sans']">
              <thead className="bg-[#F4F2EE] text-[10px] uppercase tracking-[0.12em] text-[#8A8681]">
                <tr>
                  <th className="px-4 py-2 text-left">Lead</th>
                  <th className="px-4 py-2 text-left">RSS</th>
                  <th className="px-4 py-2 text-left">Perception</th>
                  <th className="px-4 py-2 text-left">Last depth</th>
                  <th className="px-4 py-2 text-left">Ecosystem</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-6 text-[#8A8681]">No leads match.</td></tr>
                ) : filtered.map(l => (
                  <tr key={l.id} data-testid={`pipeline-row-${l.id}`} className="border-t border-[#EBE8E3] hover:bg-[#FBFaf9]">
                    <td className="px-4 py-3">
                      <div className="text-[#2C2A29]">{l.name || '—'}</div>
                      <div className="text-[11px] text-[#8A8681]">{l.email || l.phone || ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      {l.rss_band ? (
                        <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${BAND_TONE[l.rss_band] || ''}`}>
                          {l.rss_band.replace('_', ' ')} · {l.rss_composite ?? '—'}
                        </span>
                      ) : <span className="text-[11px] text-[#B5B0A8]">not scored</span>}
                    </td>
                    <td className="px-4 py-3">
                      {l.perception_state ? (
                        <span className="text-[10px] uppercase tracking-[0.14em] bg-[#F4F2EE] text-[#5C5955] rounded-full px-2 py-0.5">
                          {l.perception_state}
                        </span>
                      ) : <span className="text-[11px] text-[#B5B0A8]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <ConversationDepthBadge code={l.last_conversation_depth} tier={tierMap[l.last_conversation_depth]?.tier} label={tierMap[l.last_conversation_depth]?.label} />
                      {leakMap[l.id] && (
                        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`pipeline-leaks-${l.id}`}>
                          {leakMap[l.id].categories.slice(0, 2).map(c => (
                            <LeakChip key={c} code={c} compact />
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {ecoMap[l.ecosystem_code] && <EcosystemBadge ecosystem={ecoMap[l.ecosystem_code]} compact />}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline"
                              data-testid={`pipeline-log-conv-btn-${l.id}`}
                              onClick={() => setLogFor(l)}
                              className="border-[#D8D4CE] text-[#5C5955] text-[11px] rounded-full px-3 h-7">
                        <MessageSquare className="w-3 h-3" /> Log conversation
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {logFor && (
          <ConversationLogModal lead={logFor} tiers={tiers}
                                onClose={() => setLogFor(null)}
                                onSaved={() => { setLogFor(null); reload(); }} />
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function ConversationLogModal({ lead, tiers, onClose, onSaved }) {
  const [depth, setDepth] = useState('');
  const [summary, setSummary] = useState('');
  const [next, setNext] = useState('');
  const [decode, setDecode] = useState(false);
  const [reali, setReali] = useState(false);
  const [busy, setBusy] = useState(false);
  const [path, setPath] = useState(null);

  React.useEffect(() => {
    if (!lead?.id) return;
    const url = `${API}/terra-growth/intelligence/movement-path?ecosystem_code=${lead.ecosystem_code || ''}&perception_state=${lead.perception_state || ''}`;
    axios.get(url).then(r => setPath(r.data)).catch(() => setPath(null));
  }, [lead]);

  const submit = async () => {
    if (!depth) { toast.error('Pick a depth tier first.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/terra-growth/conversation-logs`, {
        lead_id: lead.id, depth_code: depth,
        summary: summary || null, next_action: next || null,
        decode_intent_signaled: decode, realization_captured: reali,
      });
      toast.success('Conversation logged.');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()}
           data-testid="conversation-log-modal">
        <h3 className="text-lg font-['Outfit'] text-[#2C2A29]">Log conversation · {lead.name}</h3>
        {path && <MovementPathCard path={path} compact />}
        <select value={depth} onChange={(e) => setDepth(e.target.value)}
                data-testid="conversation-log-depth-select"
                className="w-full rounded-md border border-[#D8D4CE] px-3 py-2 text-sm">
          <option value="">— choose depth tier —</option>
          {tiers.map(t => <option key={t.code} value={t.code}>T{t.tier} · {t.label}</option>)}
        </select>
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)}
                  data-testid="conversation-log-summary-input"
                  placeholder="What was the conversation about? What shifted?"
                  rows={3}
                  className="w-full rounded-md border border-[#D8D4CE] px-3 py-2 text-sm" />
        <Input value={next} onChange={(e) => setNext(e.target.value)}
               data-testid="conversation-log-next-input"
               placeholder="Next action (e.g. family meeting, DECODE booking)" />
        <div className="flex items-center gap-4 text-xs text-[#5C5955]">
          <label className="inline-flex items-center gap-1">
            <input type="checkbox" checked={reali} onChange={(e) => setReali(e.target.checked)} /> Realization captured
          </label>
          <label className="inline-flex items-center gap-1">
            <input type="checkbox" checked={decode} onChange={(e) => setDecode(e.target.checked)} /> DECODE intent signaled
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="border-[#D8D4CE] text-[#5C5955] text-xs rounded-full px-4">Cancel</Button>
          <Button data-testid="conversation-log-save-btn" onClick={submit} disabled={busy}
                  className="bg-[#4A5D4E] hover:bg-[#3D4F40] text-white text-xs rounded-full px-4">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Save log
          </Button>
        </div>
      </div>
    </div>
  );
}
